import { useAuthStore } from '@/stores/auth/auth-store';
import { getCurrentAccessToken } from '@/services/api/auth-session';
import {
  useOrganizationStore,
  type Organization,
} from '@/stores/organization/organization-store';

type WorkflowContext =
  | { type: 'user'; userId?: string }
  | { type: 'organization'; orgSlug?: string; userId?: string }
  | null;

const getRecordString = (value: unknown, key: string): string | null => {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const property = Reflect.get(value, key);
  return typeof property === 'string' && property.trim() ? property : null;
};

// Get authentication token from storage
export const getAuthToken = (): string | null =>
  getCurrentAccessToken();

export const getCurrentWorkflowUserId = (): string | null => {
  const authUser = useAuthStore.getState().user;
  return getRecordString(authUser, 'id') ?? getRecordString(authUser, '_id');
};

const getVisibleOrganizations = (): Organization[] => {
  if (!useOrganizationStore?.getState) {
    return [];
  }

  const { organizations, allOrganizations, platformAdminEntitlement } =
    useOrganizationStore.getState();
  return platformAdminEntitlement
    ? [...organizations, ...allOrganizations]
    : organizations;
};

const findValidatedOrganizationForCurrentContext = (): Organization | null => {
  if (!useOrganizationStore?.getState) {
    return null;
  }

  const store = useOrganizationStore.getState();
  const {
    currentContext,
    organizations,
    allOrganizations,
    platformAdminEntitlement,
    clearCurrentContext,
    switchToOrgContext,
  } = store;

  if (currentContext?.type !== 'organization') {
    return null;
  }

  const currentSlug =
    typeof currentContext.orgSlug === 'string' && currentContext.orgSlug.trim()
      ? currentContext.orgSlug.trim()
      : null;
  const currentId =
    typeof currentContext.id === 'string' && currentContext.id.trim()
      ? currentContext.id.trim()
      : null;

  const visibleOrganizations = getVisibleOrganizations();
  const organizationBySlug = currentSlug
    ? visibleOrganizations.find((organization) => organization.slug === currentSlug) ?? null
    : null;
  const organizationById = currentId
    ? visibleOrganizations.find((organization) => organization._id === currentId) ?? null
    : null;

  if (
    organizationBySlug &&
    organizationById &&
    organizationBySlug._id !== organizationById._id
  ) {
    clearCurrentContext();
    return null;
  }

  const validatedOrganization = organizationBySlug ?? organizationById;
  if (validatedOrganization) {
    if (
      currentContext.orgSlug !== validatedOrganization.slug ||
      currentContext.id !== validatedOrganization._id ||
      currentContext.displayName !== validatedOrganization.name
    ) {
      switchToOrgContext(validatedOrganization.slug);
    }
    return validatedOrganization;
  }

  const isPendingPlatformOrganizationValidation =
    platformAdminEntitlement &&
    allOrganizations.length === 0 &&
    !organizations.some((organization) => organization.slug === currentSlug);

  if (isPendingPlatformOrganizationValidation) {
    return null;
  }

  clearCurrentContext();
  return null;
};

export const getValidatedCurrentWorkflowOrganization = (): Organization | null =>
  findValidatedOrganizationForCurrentContext();

// Build the correct workflow API route based on workspace context
export const buildWorkflowApiRoute = (
  _currentContext: WorkflowContext,
  workflowId?: string,
): string => {
  const organization = getValidatedCurrentWorkflowOrganization();

  const baseRoute = organization
    ? `api/orgs/${organization.slug}/workflows`
    : (() => {
        const userId = getCurrentWorkflowUserId();
        if (!userId) {
          throw new Error(
            'Your session is missing required credentials. Please log out and log back in to refresh your authentication.',
          );
        }
        return `api/users/${userId}/workflows`;
      })();

  return workflowId ? `${baseRoute}/${workflowId}` : baseRoute;
};

export const buildWorkflowUserApiRoute = (
  resource: string,
  userId = getCurrentWorkflowUserId(),
): string => {
  if (!userId) {
    throw new Error(
      'Your session is missing required credentials. Please log out and log back in to refresh your authentication.',
    );
  }

  const normalizedResource = resource.replace(/^\/+/, '');
  return `api/users/${userId}/${normalizedResource}`;
};

export const buildWorkflowOrganizationApiRoute = (
  organizationSlug: string,
  resource: string,
): string => {
  const normalizedResource = resource.replace(/^\/+/, '');
  return `api/orgs/${organizationSlug}/${normalizedResource}`;
};
